/**
 * BroadcastBell - the header bell that makes a dismissed announcement
 * reachable again.
 *
 * The banner is the only surface a broadcast has, and dismissing it is
 * permanent for that revision. That is correct for "Vera deploys at 6pm" and
 * wrong the moment someone waves away a message and then wants it back. This
 * bell closes that hole: while a broadcast is live, it stays reachable.
 *
 * THE DOT IS NOT AN UNREAD BADGE. It means "something is live that you cannot
 * currently see", so it does NOT clear when the popover is opened, unlike the
 * Ideas notifications bell. It clears when the broadcast expires, is edited
 * into a new revision (which re-surfaces the strip anyway), or is pulled. That
 * difference is the whole reason this is not the Ideas bell with a new feed.
 *
 * Renders NOTHING when there is nothing live for this person: no permanent
 * empty furniture in a 48px header that already carries a Dev Data pill.
 *
 * Rides the same React Query cache entry as the banner, so it costs no extra
 * polling and the two can never disagree about what is live.
 *
 * Design of record: docs/design/broadcast/PLAN.md section 13
 */

import { Link } from 'react-router-dom';
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  AlertRegular,
  InfoRegular,
  WarningRegular,
  ErrorCircleRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../../../providers/AuthProvider';
import { veracityColors } from '../../../ui';
import { useBroadcasts, type BroadcastNotice, type BroadcastSeverity } from '../model/useBroadcasts';

const useStyles = makeStyles({
  wrap: {
    position: 'relative',
    display: 'inline-flex',
    // Chrome, like the banner: it has no place on a printed plan packet.
    '@media print': {
      display: 'none',
    },
  },
  // Mirrors AppShell's headerIconBtn so the bell sits level with the zoom and
  // help buttons. Colour is inherited, which is what lets one component serve
  // the dark AppShell header and the white launcher header.
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    padding: '0',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'inherit',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(127,127,127,0.18)',
    },
  },
  dot: {
    position: 'absolute',
    top: '7px',
    right: '7px',
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    // A ring so the dot reads on both the dark and the white header without
    // knowing which one it is on.
    boxShadow: '0 0 0 1.5px rgba(255,255,255,0.85)',
    pointerEvents: 'none',
  },
  dotInfo: { backgroundColor: veracityColors.duskBlue },
  dotWarning: { backgroundColor: veracityColors.daybreakAmber },
  dotCritical: { backgroundColor: veracityColors.morningCoral },

  surface: {
    minWidth: '320px',
    maxWidth: '380px',
    maxHeight: '420px',
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
  },
  heading: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  item: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  itemIcon: {
    display: 'inline-flex',
    flexShrink: 0,
    marginTop: '2px',
  },
  iconInfo: { color: veracityColors.duskBlue },
  iconWarning: { color: veracityColors.daybreakAmber },
  iconCritical: { color: veracityColors.morningCoral },
  itemBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: '0',
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    // Long messages wrap here rather than truncating: the popover is where
    // someone goes to actually READ the thing they dismissed.
    overflowWrap: 'anywhere',
  },
  link: {
    alignSelf: 'flex-start',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.veracityOrange,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  onBanner: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
});

const ICONS: Record<BroadcastSeverity, typeof InfoRegular> = {
  info: InfoRegular,
  warning: WarningRegular,
  critical: ErrorCircleRegular,
};

/** Critical outranks warning outranks info, so the dot shows the worst thing hidden. */
function topSeverity(notices: readonly BroadcastNotice[]): BroadcastSeverity {
  if (notices.some((n) => n.severity === 'critical')) return 'critical';
  if (notices.some((n) => n.severity === 'warning')) return 'warning';
  return 'info';
}

function BellItem({ notice, onBanner }: { notice: BroadcastNotice; onBanner: boolean }) {
  const styles = useStyles();
  const Icon = ICONS[notice.severity];
  const isInternal = notice.linkUrl?.startsWith('/') ?? false;
  const iconTone =
    notice.severity === 'critical'
      ? styles.iconCritical
      : notice.severity === 'warning'
        ? styles.iconWarning
        : styles.iconInfo;

  return (
    <div className={styles.item}>
      <span className={mergeClasses(styles.itemIcon, iconTone)}>
        <Icon fontSize={16} />
      </span>
      <div className={styles.itemBody}>
        <span className={styles.message}>{notice.message}</span>
        {notice.linkUrl &&
          (isInternal ? (
            <Link to={notice.linkUrl} className={styles.link}>
              {notice.linkLabel || 'Open'}
            </Link>
          ) : (
            <a
              href={notice.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              {notice.linkLabel || 'Open'}
            </a>
          ))}
        {onBanner && <span className={styles.onBanner}>Showing on your banner</span>}
      </div>
    </div>
  );
}

export function BroadcastBell({ className }: { className?: string }) {
  const styles = useStyles();
  const { impersonation } = useAuth();
  const { data } = useBroadcasts();

  // Same rule as the banner: you are inspecting someone else's view, not
  // standing in the audience.
  if (impersonation) return null;

  const showing = data?.notices ?? [];
  const hidden = data?.dismissed ?? [];
  // Nothing live for this person means no bell at all.
  if (showing.length === 0 && hidden.length === 0) return null;

  const label =
    hidden.length > 0
      ? `Announcements (${hidden.length} dismissed, still active)`
      : 'Announcements';

  return (
    <Popover positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <span className={styles.wrap}>
          <button
            type="button"
            className={mergeClasses(styles.button, className)}
            aria-label={label}
            title={label}
          >
            <AlertRegular style={{ fontSize: '20px' }} />
          </button>
          {hidden.length > 0 && (
            <span
              className={mergeClasses(
                styles.dot,
                topSeverity(hidden) === 'critical'
                  ? styles.dotCritical
                  : topSeverity(hidden) === 'warning'
                    ? styles.dotWarning
                    : styles.dotInfo,
              )}
            />
          )}
        </span>
      </PopoverTrigger>

      <PopoverSurface className={styles.surface}>
        <div className={styles.heading}>
          {hidden.length > 0 ? 'Still active' : 'Active announcements'}
        </div>
        {/* Dismissed first: they are the reason someone opened this. */}
        {hidden.map((n) => (
          <BellItem key={n.id} notice={n} onBanner={false} />
        ))}
        {showing.map((n) => (
          <BellItem key={n.id} notice={n} onBanner />
        ))}
      </PopoverSurface>
    </Popover>
  );
}

export default BroadcastBell;
